/**
 * Request Version History API client (#1148). Endpoints are relative to `/api`.
 */

import { api } from './api';

import type {
  RequestVersionDetail,
  RequestVersionFieldHistoryResponse,
  RequestVersionListParams,
  RequestVersionListResponse,
  RequestVersionRestoreRequest,
  RequestVersionRestoreResponse,
} from '../types/RequestVersion';

const buildQuery = (params: RequestVersionListParams = {}): string => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  if (params.createdBy) query.set('createdBy', params.createdBy);
  if (params.orderBy) query.set('orderBy', params.orderBy);
  if (params.orderDirection) query.set('orderDirection', params.orderDirection);
  (params.source ?? []).forEach(value => query.append('source', value));
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
};

export const requestVersionsService = {
  getVersions: (requestId: number, params?: RequestVersionListParams) =>
    api.get<RequestVersionListResponse>(
      `/requests/${requestId}/versions${buildQuery(params)}`,
    ),

  getVersionDetail: (requestId: number, versionId: number) =>
    api.get<RequestVersionDetail>(
      `/requests/${requestId}/versions/${versionId}`,
    ),

  restoreVersion: (
    requestId: number,
    versionId: number,
    body: RequestVersionRestoreRequest,
  ) =>
    api.post<RequestVersionRestoreResponse>(
      `/requests/${requestId}/versions/${versionId}/restore`,
      body,
    ),

  getFieldHistory: (
    requestId: number,
    fieldPath: string,
    params?: RequestVersionListParams,
  ) => {
    const query = new URLSearchParams();
    query.set('fieldPath', fieldPath);
    query.set('page', String(params?.page ?? 1));
    query.set('pageSize', String(params?.pageSize ?? 50));
    if (params?.orderBy) query.set('orderBy', params.orderBy);
    if (params?.orderDirection) {
      query.set('orderDirection', params.orderDirection);
    }
    return api.get<RequestVersionFieldHistoryResponse>(
      `/requests/${requestId}/versions/field-history?${query.toString()}`,
    );
  },
};
